import { useState, type FormEvent } from "react";
import type { InspectorConfig } from "../api";
import { answerChallenge, describeAuthError, startSignIn, type SignInStep } from "./session";

// The sign-in screen. Four states, in the order the pool asks for them:
// credentials → a new password on first sign-in (accounts are created by an
// administrator, which always sets a temporary one) → enrolling an
// authenticator app → the six-digit code.

export function SignIn({
  config,
  onSignedIn,
  notice,
}: {
  config: InspectorConfig;
  onSignedIn: () => void;
  /** Why the screen is back, when a session ended under an open page. */
  notice?: string;
}): JSX.Element {
  const [step, setStep] = useState<SignInStep>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<SignInStep>): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const next = await action();
      setAnswer("");
      if (next.kind === "done") onSignedIn();
      else setStep(next);
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  const submitCredentials = (event: FormEvent): void => {
    event.preventDefault();
    void run(() => startSignIn(email.trim(), password));
  };

  const submitAnswer = (event: FormEvent): void => {
    event.preventDefault();
    void run(() => answerChallenge(answer.trim()));
  };

  return (
    <div className="gate">
      <div className="gate-card">
        <div className="gate-brand">
          <span className="gate-mark">
            Betalings<span>service</span>
          </span>
          <span className="stage-chip" data-stage={config.stage} data-on-paper="true">
            {config.stage}
          </span>
        </div>

        {step.kind === "credentials" ? (
          <form onSubmit={submitCredentials}>
            <h1>Files</h1>
            {notice ? <p className="error">{notice}</p> : null}
            <p>Read the files exchanged with the bank. Accounts are created by an administrator.</p>
            <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="username" autoFocus />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
            />
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" className="button" disabled={busy || email === "" || password === ""}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : null}

        {step.kind === "new_password" ? (
          <form onSubmit={submitAnswer}>
            <h1>Choose a password</h1>
            <p>This account still has the temporary password it was created with. Set your own to continue.</p>
            <Field
              label="New password"
              type="password"
              value={answer}
              onChange={setAnswer}
              autoComplete="new-password"
              autoFocus
            />
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" className="button" disabled={busy || answer === ""}>
              {busy ? "Saving…" : "Save and continue"}
            </button>
          </form>
        ) : null}

        {step.kind === "totp_setup" ? (
          <form onSubmit={submitAnswer}>
            <h1>Set up your authenticator</h1>
            <p>
              Add this account to an authenticator app, then enter the code it shows. Paste the setup link, or type the
              key by hand.
            </p>
            <label className="form-field">
              <span className="label">Setup key</span>
              <input className="mono" readOnly value={step.secret} onFocus={(event) => event.target.select()} />
            </label>
            <label className="form-field">
              <span className="label">Setup link</span>
              <input className="mono" readOnly value={step.uri} onFocus={(event) => event.target.select()} />
            </label>
            <Field label="Code from the app" value={answer} onChange={setAnswer} inputMode="numeric" autoFocus />
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" className="button" disabled={busy || answer.length < 6}>
              {busy ? "Checking…" : "Confirm"}
            </button>
          </form>
        ) : null}

        {step.kind === "totp_code" ? (
          <form onSubmit={submitAnswer}>
            <h1>Enter your code</h1>
            <p>The six-digit code from your authenticator app.</p>
            <Field label="Code" value={answer} onChange={setAnswer} inputMode="numeric" autoFocus />
            {error ? <p className="error">{error}</p> : null}
            <button type="submit" className="button" disabled={busy || answer.length < 6}>
              {busy ? "Checking…" : "Continue"}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
  inputMode,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  inputMode?: "numeric";
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <label className="form-field">
      <span className="label">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        autoFocus={autoFocus}
      />
    </label>
  );
}
